'use client';

import { type ComponentProps, useLayoutEffect, useRef, useState } from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { cn } from '@ifixit/ui/lib/utils';
import { FaIcon } from './icon';
import { usePortalContainer } from './portal-container';

type SelectProps = ComponentProps<typeof SelectPrimitive.Root> & {
   keepOpenOnResize?: boolean;
};

function Select({ keepOpenOnResize = false, ...props }: SelectProps) {
   if (!keepOpenOnResize) {
      return <SelectPrimitive.Root data-slot="select" {...props} />;
   }
   return <ResizePersistentSelect {...props} />;
}

/**
 * On a phone, folding the keyboard resizes the window and makes Radix close
 * the list that just opened. The composer uses SelectContent's default popper
 * positioning, which follows the resize, so a close with no pointer or key
 * input since the last resize is dropped.
 */
function ResizePersistentSelect({
   open: openProp,
   defaultOpen = false,
   onOpenChange,
   ...props
}: ComponentProps<typeof SelectPrimitive.Root>) {
   const [openState, setOpenState] = useState(defaultOpen);
   const open = openProp ?? openState;
   const resizedSinceInputRef = useRef(false);

   // A layout effect registers before Radix's own resize listener, a passive
   // effect of the listbox, so the flag is up by the time Radix asks to close.
   useLayoutEffect(() => {
      if (!open) return;
      resizedSinceInputRef.current = false;
      function flagResize() {
         resizedSinceInputRef.current = true;
      }
      function clearResize() {
         resizedSinceInputRef.current = false;
      }
      // Activation can arrive as a bare click (voice control, assistive tech)
      // or complete on pointerup, so every step of an input clears the flag.
      const inputEvents = ['pointerdown', 'pointerup', 'click', 'keydown'];
      window.addEventListener('resize', flagResize);
      window.addEventListener('blur', clearResize);
      for (const type of inputEvents) {
         document.addEventListener(type, clearResize, { capture: true });
      }
      return () => {
         window.removeEventListener('resize', flagResize);
         window.removeEventListener('blur', clearResize);
         for (const type of inputEvents) {
            document.removeEventListener(type, clearResize, { capture: true });
         }
      };
   }, [open]);

   function handleOpenChange(next: boolean) {
      if (!next && resizedSinceInputRef.current) return;
      if (openProp === undefined) setOpenState(next);
      onOpenChange?.(next);
   }

   return (
      <SelectPrimitive.Root
         data-slot="select"
         open={open}
         onOpenChange={handleOpenChange}
         {...props}
      />
   );
}

function SelectGroup({ ...props }: ComponentProps<typeof SelectPrimitive.Group>) {
   return <SelectPrimitive.Group data-slot="select-group" {...props} />;
}

function SelectValue({ ...props }: ComponentProps<typeof SelectPrimitive.Value>) {
   return <SelectPrimitive.Value data-slot="select-value" {...props} />;
}

function SelectTrigger({
   className,
   size = 'default',
   children,
   ...props
}: ComponentProps<typeof SelectPrimitive.Trigger> & {
   size?: 'sm' | 'default';
}) {
   return (
      <SelectPrimitive.Trigger
         data-slot="select-trigger"
         data-size={size}
         className={cn(
            "border-input data-[placeholder]:text-muted-foreground [&_svg:not([class*='text-'])]:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 aria-invalid:border-destructive flex w-fit items-center justify-between gap-2 rounded-lg border bg-white active:bg-gray-100 px-3 py-2 text-sm whitespace-nowrap transition-[color,background-color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 data-[size=default]:h-9 data-[size=sm]:h-8 *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
            className
         )}
         {...props}
      >
         {children}
         <SelectPrimitive.Icon asChild>
            <FaIcon name="caret-down" className="size-4 opacity-50" />
         </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
   );
}

function SelectContent({
   className,
   children,
   position = 'popper',
   ...props
}: ComponentProps<typeof SelectPrimitive.Content>) {
   const container = usePortalContainer();
   return (
      <SelectPrimitive.Portal container={container ?? undefined}>
         <SelectPrimitive.Content
            data-slot="select-content"
            className={cn(
               'bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 max-h-(--radix-select-content-available-height) max-w-(--radix-select-content-available-width) min-w-[8rem] origin-(--radix-select-content-transform-origin) overflow-x-hidden overflow-y-auto rounded-lg border shadow-md',
               position === 'popper' &&
                  'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
               className
            )}
            position={position}
            {...props}
         >
            <SelectScrollUpButton />
            <SelectPrimitive.Viewport
               className={cn(
                  'p-1',
                  position === 'popper' &&
                     'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)] scroll-my-1'
               )}
            >
               {children}
            </SelectPrimitive.Viewport>
            <SelectScrollDownButton />
         </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
   );
}

function SelectLabel({ className, ...props }: ComponentProps<typeof SelectPrimitive.Label>) {
   return (
      <SelectPrimitive.Label
         data-slot="select-label"
         className={cn('text-muted-foreground px-2 py-1.5 text-xs', className)}
         {...props}
      />
   );
}

function SelectItem({
   className,
   children,
   ...props
}: ComponentProps<typeof SelectPrimitive.Item>) {
   return (
      <SelectPrimitive.Item
         data-slot="select-item"
         className={cn(
            "focus:bg-accent focus:text-accent-foreground [&_svg:not([class*='text-'])]:text-muted-foreground relative flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:min-w-0 *:[span]:last:items-center *:[span]:last:gap-2",
            className
         )}
         {...props}
      >
         <span className="absolute right-2 flex size-3.5 items-center justify-center">
            <SelectPrimitive.ItemIndicator>
               <FaIcon name="check" className="size-4" />
            </SelectPrimitive.ItemIndicator>
         </span>
         <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
      </SelectPrimitive.Item>
   );
}

function SelectSeparator({
   className,
   ...props
}: ComponentProps<typeof SelectPrimitive.Separator>) {
   return (
      <SelectPrimitive.Separator
         data-slot="select-separator"
         className={cn('bg-border pointer-events-none -mx-1 my-1 h-px', className)}
         {...props}
      />
   );
}

function SelectScrollUpButton({
   className,
   ...props
}: ComponentProps<typeof SelectPrimitive.ScrollUpButton>) {
   return (
      <SelectPrimitive.ScrollUpButton
         data-slot="select-scroll-up-button"
         className={cn('flex cursor-default items-center justify-center py-1', className)}
         {...props}
      >
         <FaIcon name="chevron-up" className="size-4" />
      </SelectPrimitive.ScrollUpButton>
   );
}

function SelectScrollDownButton({
   className,
   ...props
}: ComponentProps<typeof SelectPrimitive.ScrollDownButton>) {
   return (
      <SelectPrimitive.ScrollDownButton
         data-slot="select-scroll-down-button"
         className={cn('flex cursor-default items-center justify-center py-1', className)}
         {...props}
      >
         <FaIcon name="chevron-down" className="size-4" />
      </SelectPrimitive.ScrollDownButton>
   );
}

export {
   Select,
   SelectContent,
   SelectGroup,
   SelectItem,
   SelectLabel,
   SelectScrollDownButton,
   SelectScrollUpButton,
   SelectSeparator,
   SelectTrigger,
   SelectValue,
};
